import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { PageHeader, Panel, Stat, StatGrid } from "@/components/primitives";
import { useYard } from "@/lib/use-yard";
import { fmtDate, type Dispute } from "@/lib/yard-data";

export const Route = createFileRoute("/disputes/")({
  head: () => ({
    meta: [
      { title: "Disputes — PortDwellr" },
      { name: "description", content: "Detention, demurrage and damage disputes, each backed by the full timestamped record from RESOLVE." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: DisputesList,
});

const FILTERS: Array<{ key: Dispute["status"] | "all"; label: string }> = [
  { key: "all", label: "All" },
  { key: "open", label: "Open" },
  { key: "under_review", label: "Under review" },
  { key: "resolved", label: "Resolved" },
  { key: "escalated", label: "Escalated" },
];

export const STATUS_TEXT: Record<Dispute["status"], string> = {
  open: "Open",
  under_review: "Under review",
  resolved: "Resolved",
  escalated: "Escalated",
};

function DisputesList() {
  const { disputes } = useYard();
  const [filter, setFilter] = useState<Dispute["status"] | "all">("all");
  const rows = disputes.filter((d) => filter === "all" || d.status === filter);
  const exposure = disputes.filter((d) => d.status !== "resolved").reduce((a, d) => a + d.amount, 0);

  return (
    <>
      <PageHeader
        eyebrow="RESOLVE · the record"
        title="Disputes"
        lead="Every dispute here opens onto the timestamp trail for that container. The trail is the resolution — arrival, assignment, unload start and end, departure, all recorded by the system rather than remembered by whoever worked the gate."
      />

      <StatGrid>
        <Stat value={disputes.filter((d) => d.status === "open").length} label="Open disputes" sub="awaiting response" />
        <Stat value={`$${exposure.toLocaleString()}`} tone="signal" label="Unresolved claim exposure" sub="detention + damage + billing" />
        <Stat
          value={`${Math.round((disputes.filter((d) => d.status === "resolved").length / disputes.length) * 100)}%`}
          label="Closed with the record alone"
          sub="no escalation needed"
        />
      </StatGrid>

      <div className="mt-10 flex flex-wrap gap-0 border border-line">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`border-r border-line px-4 py-3 font-mono text-[11px] uppercase tracking-[0.1em] last:border-r-0 ${
              filter === f.key ? "bg-steel text-paper" : "bg-paper-raised text-steel hover:bg-paper"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        <Panel title={`${rows.length} dispute${rows.length === 1 ? "" : "s"}`}>
          <ul>
            {rows.map((d) => (
              <li key={d.id} className="border-b border-line last:border-b-0">
                <Link
                  to="/disputes/$disputeId"
                  params={{ disputeId: d.id }}
                  className="block px-5 py-5 hover:bg-paper md:px-6"
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <span className="font-mono text-[12px] uppercase tracking-[0.1em] text-signal-deep">
                      {d.id} · {d.type}
                    </span>
                    <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-steel-mid">
                      {STATUS_TEXT[d.status]} · opened {fmtDate(d.openedAt)}
                    </span>
                  </div>
                  <div className="mt-2 font-display text-[17px] font-semibold">
                    {d.container} · {d.carrier}
                  </div>
                  <p className="mt-1 max-w-[70ch] text-[14px] text-ink-soft">{d.summary}</p>
                  <div className="mt-3 font-mono text-[13px] text-steel">
                    {d.amount > 0 ? `$${d.amount.toLocaleString()} claimed` : "No amount outstanding"}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </Panel>
      </div>
    </>
  );
}
